'use client'
import { Box, Flex, Skeleton, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import React, { Suspense } from 'react'

import PriceChange from '@/components/PriceChange'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { useGlobalMetrics } from '@/services/coin/market'
import { formatMarketCap, formatPercentage } from '@/utils/number/format'

import MetricItem from '../MetricItem'

function GlobalMetricsFallback() {
  return (
    <Flex gap={4}>
      {[0, 1, 2, 3].map(index => (
        <Box key={index} w={'82.75px'} h={72} p={8} data-index={index}>
          <Stack>
            <Skeleton h={16} w={'100%'} mb={4} radius="xl" />
            <Skeleton h={36} w={'100%'} radius="xl" />
          </Stack>
        </Box>
      ))}
    </Flex>
  )
}

const GlobalMetricsContent = ({ fearGreedSlot }: { fearGreedSlot?: React.ReactNode }) => {
  const { data } = useGlobalMetrics()
  const t = useTranslations()

  const merketCap = data?.market_cap ?? 0
  const volume24H = data?.volume_24h ?? 0

  const {
    amounts: [{ formattedValue: formattedMarketCap }, { formattedValue: formattedVolume24h }],
  } = useCurrencyAmounts({
    amounts: [{ value: merketCap }, { value: volume24H }],
    valueFormatter: formatMarketCap,
    currencyFormatOptions: {},
  })

  const metrics = [
    {
      label: t('coinTable.headers.marketCap'),
      value: formattedMarketCap,
      subValue: <PriceChange value={data?.market_cap_change_rate ?? 0} variant="micro-text" />,
      labelColor: 'var(--content-secondary)',
      isMobile: true,
    },
    {
      label: t('coinTable.headers.24hVolume'),
      labelColor: 'var(--content-secondary)',
      value: formattedVolume24h,
      isMobile: true,
    },
    {
      label: t('globalMetrics.dominance'),
      labelColor: 'var(--content-secondary)',
      value: `${formatPercentage((data?.btc_dominance_rate ?? 0) * 100)}%`,
      subValue: (
        <Text c={'var(--content-secondary)'} variant="micro-text">
          BTC
        </Text>
      ),
      isMobile: true,
    },
    {
      label: t('globalMetrics.fearGreed'),
      labelColor: 'var(--content-secondary)',
      value: fearGreedSlot ?? null,
      isMobile: true,
      isFearGreed: true,
    },
  ]

  return (
    <Flex gap={4}>
      {metrics.map((item, index) => (
        <Box
          p={8}
          data-index={index}
          key={item.label}
          miw={85}
          h={72}
          bg={'var(--bg-surface-secondary)'}
          bdrs={8}
        >
          <MetricItem {...item} />
        </Box>
      ))}
    </Flex>
  )
}

export const GlobalMetricsContentMobile = ({
  fearGreedSlot,
}: {
  fearGreedSlot?: React.ReactNode
}) => {
  return (
    <Suspense fallback={<GlobalMetricsFallback />}>
      <GlobalMetricsContent fearGreedSlot={fearGreedSlot} />
    </Suspense>
  )
}
